import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeftIcon, 
  PlayIcon, 
  PauseIcon, 
  CogIcon,

  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { BracketVisualization } from '../../components/tournament/BracketVisualization';
import MatchResultModal from '../../components/tournament/MatchResultModal';
import TournamentControlPanel from '../../components/tournament/TournamentControlPanel';
import { tournamentsApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface Tournament {
  id: number;
  name: string;
  description: string;
  competition_id: number;
  competition_name: string;
  type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss';
  format: 'bracket' | 'group_stage' | 'hybrid';
  max_participants?: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  participant_count: number;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
}

interface Match {
  id: string;
  tournament_id: number;
  round: number;
  position: number;
  participant1Id?: number;
  participant2Id?: number;
  team1Id?: number;
  team2Id?: number;
  score1?: number;
  score2?: number;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_at?: string;
  location?: string;
  winner_id?: number;
}

interface Participant {
  id: number;
  name: string;
  seed: number;
  team_id?: number;
  status: 'active' | 'eliminated' | 'bye';
}

const TournamentBracket: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedView, setSelectedView] = useState<'bracket' | 'schedule' | 'participants' | 'control'>('bracket');
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);

  // Fetch tournament data
  const { data: tournament, isLoading: tournamentLoading, isError: tournamentError, error: tournamentErrorData } = useQuery<Tournament>(
    ['tournament', tournamentId],
    () => tournamentsApi.getById(parseInt(tournamentId!)),
    {
      enabled: !!tournamentId,
      onError: (error: any) => {
        console.error('❌ Failed to load tournament:', error);
        toast.error('Failed to load tournament data');
      }
    }
  );

  // Fetch matches
  const { data: matches = [], isLoading: matchesLoading, isError: matchesError, error: matchesErrorData } = useQuery<any[]>(
    ['tournament-matches', tournamentId],
    () => tournamentsApi.getMatches(parseInt(tournamentId!)),
    {
      enabled: !!tournamentId,
      onError: (error: any) => {
        console.error('❌ Failed to load matches:', error);
        toast.error('Failed to load matches');
      }
    }
  );

  // Fetch participants
  const { data: participants = [], isLoading: participantsLoading, isError: participantsError, error: participantsErrorData } = useQuery<any[]>(
    ['tournament-participants', tournamentId],
    () => tournamentsApi.getParticipants(parseInt(tournamentId!)),
    {
      enabled: !!tournamentId,
      onError: (error: any) => {
        console.error('❌ Failed to load participants:', error);
        toast.error('Failed to load participants');
      }
    }
  );

  // Update match mutation
  const updateMatchMutation = useMutation(
    ({ matchId, updates }: { matchId: string; updates: Partial<Match> }) =>
      tournamentsApi.updateMatch(matchId, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tournament-matches', tournamentId]);
        toast.success('Match updated successfully');
      },
      onError: () => {
        toast.error('Failed to update match');
      }
    }
  );

  // Move participant mutation
  const moveParticipantMutation = useMutation(
    ({ participantId, newPosition }: { participantId: number; newPosition: number }) =>
      tournamentsApi.moveParticipant(participantId, newPosition),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tournament-participants', tournamentId]);
        toast.success('Participant moved successfully');
      },
      onError: () => {
        toast.error('Failed to move participant');
      }
    }
  );

  const handleMatchClick = (matchId: string) => {
    const match = matches?.find(m => m.id === matchId);
    if (match && tournament) {
      // Ensure match has required properties for the Match type
      const completeMatch: Match = {
        ...match,
        tournamentId: tournament.id,
        // Ensure all required properties are present
        round: match.round || 1,
        position: match.position || 0,
        status: match.status || 'pending',
        scheduledAt: match.scheduledAt ? new Date(match.scheduledAt) : undefined,
        startedAt: match.startedAt ? new Date(match.startedAt) : undefined,
        completedAt: match.completedAt ? new Date(match.completedAt) : undefined
      };
      setSelectedMatch(completeMatch);
      setShowMatchModal(true);
    }
  };

  const handleMatchUpdate = (matchId: string, updates: Partial<Match>) => {
    updateMatchMutation.mutate({ matchId, updates });
    setShowMatchModal(false);
    setSelectedMatch(null);
  };

  const handleParticipantMove = (participantId: number, newPosition: number) => {
    moveParticipantMutation.mutate({ participantId, newPosition });
  };

  // Tournament control functions - to be implemented
  // const handleStartTournament = () => {
  //   toast.success('Tournament started!');
  // };

  // const handlePauseTournament = () => {
  //   toast.success('Tournament paused!');
  // };

  // Show loading only if tournament is loading
  if (tournamentLoading) {
    return <LoadingSpinner />;
  }

  // Show error if tournament failed to load
  if (tournamentError || !tournament) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Tournament not found</h2>
        <p className="text-gray-600 mt-2">{(tournamentErrorData as any)?.message || 'Unable to load tournament data'}</p>
        <button
          onClick={() => navigate('/admin/tournaments')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/tournaments')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{tournament.name}</h1>
                <p className="text-sm text-gray-500">{tournament.competition_name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Tournament Stats */}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <UserGroupIcon className="h-4 w-4" />
                  <span>{tournament.participant_count} participants</span>
                </div>
                <div className="flex items-center space-x-1">
                  <TrophyIcon className="h-4 w-4" />
                  <span className="capitalize">{tournament.type.replace('_', ' ')}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <button
                onClick={() => setIsLiveMode(!isLiveMode)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  isLiveMode
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {isLiveMode ? (
                  <>
                    <PauseIcon className="h-4 w-4 inline mr-1" />
                    Pause
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 inline mr-1" />
                    Start Live
                  </>
                )}
              </button>

              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <CogIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'bracket', name: 'Bracket', icon: ChartBarIcon },
              { id: 'control', name: 'Control', icon: CogIcon },
              { id: 'schedule', name: 'Schedule', icon: CalendarIcon },
              { id: 'participants', name: 'Participants', icon: UserGroupIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedView(tab.id as any)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedView === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {selectedView === 'bracket' && (
          <div className="bg-white rounded-lg shadow-sm border h-[800px]">
            <BracketVisualization
              tournament={tournament as any}
              matches={matches}
              participants={participants as any}
              onMatchUpdate={handleMatchClick}
              onParticipantMove={handleParticipantMove}
              isInteractive={true}
              showLiveScores={isLiveMode}
            />
          </div>
        )}

        {selectedView === 'control' && tournament && (
          <div className="space-y-6">
            <TournamentControlPanel
              tournament={tournament as any}
              participantCount={tournament.participant_count || 0}
              matchCount={matches?.length || 0}
              completedMatches={matches?.filter(m => m.status === 'completed').length || 0}
              onBracketGenerate={() => {
                // Refresh data after bracket generation
                queryClient.invalidateQueries(['tournament-matches', tournamentId]);
                queryClient.invalidateQueries(['tournament-participants', tournamentId]);
              }}
              onTournamentStart={() => setIsLiveMode(true)}
              onTournamentPause={() => setIsLiveMode(false)}
              onTournamentComplete={() => {
                setIsLiveMode(false);
                toast.success('Tournament completed successfully!');
              }}
            />
          </div>
        )}

        {selectedView === 'schedule' && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Tournament Schedule</h2>
            {matches && matches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Round</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Match</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participant 1</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participant 2</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {matches.map((match) => (
                      <tr key={match.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Round {match.round_number || match.round || 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Match {match.match_number || match.position || 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {participants.find(p => p.id === match.participant1_id)?.first_name} {participants.find(p => p.id === match.participant1_id)?.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {participants.find(p => p.id === match.participant2_id)?.first_name} {participants.find(p => p.id === match.participant2_id)?.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            match.status === 'completed' ? 'bg-green-100 text-green-800' :
                            match.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {match.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {match.score1 !== undefined && match.score2 !== undefined ? `${match.score1} - ${match.score2}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No matches scheduled yet. Generate a bracket to create matches.</p>
            )}
          </div>
        )}

        {selectedView === 'participants' && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Participants ({participants?.length || 0})</h2>
            {participants && participants.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Church</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participants.map((participant) => (
                      <tr key={participant.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {participant.first_name} {participant.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{participant.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{participant.church_name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            Registered
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No participants registered yet.</p>
            )}
          </div>
        )}
      </div>

      {/* Match Result Modal */}
      {selectedMatch && (
        <MatchResultModal
          match={selectedMatch as any}
          participants={(participants || []) as any}
          isOpen={showMatchModal}
          onClose={() => {
            setShowMatchModal(false);
            setSelectedMatch(null);
          }}
          onSubmit={(matchId: string, updates: any) => handleMatchUpdate(matchId, updates)}
          isLoading={updateMatchMutation.isLoading}
        />
      )}
    </div>
  );
};

export default TournamentBracket;
